import { useEffect, useState } from "react";
import { apiFetch } from "../api";
import { booting } from "../boot";
import { setSetting } from "../db/actions";
import { useQuery } from "../db/client";

/*
 * The account's name, email and photo (synced settings `profile.*`). A Google sign-in fills them from the Google
 * account (server profile.ts); the name stays editable here, and once edited Google no longer replaces it.
 * The photo's bytes come from our own API (/api/files/<sha>) and are kept in Cache Storage, so it shows offline and
 * no request goes to Google.
 */

export interface Profile {
  name: string;
  email: string;
  photo: { sha: string; mime: string } | null;
}

const KEYS = ["profile.name", "profile.email", "profile.photo"];

export function useProfile(): Profile {
  const rows = useQuery<{ id: string; value: string }>(`SELECT id, value FROM settings WHERE id IN (${KEYS.map(() => "?").join(",")}) AND deleted = 0`, KEYS, ["settings"]);
  const get = (id: string): unknown => {
    const r = rows?.find((x) => x.id === id);
    try {
      return r ? JSON.parse(r.value) : null;
    } catch {
      return null;
    }
  };
  const photo = get("profile.photo") as Profile["photo"];
  return {
    name: typeof get("profile.name") === "string" ? (get("profile.name") as string) : "",
    email: typeof get("profile.email") === "string" ? (get("profile.email") as string) : "",
    photo: photo && typeof photo.sha === "string" && /^[0-9a-f]{64}$/.test(photo.sha) ? photo : null,
  };
}

/** "Peter" from "Peter Byun", for the greeting (Evernote's "Good evening, Peter!"). */
export function firstName(name: string): string {
  return name.trim().split(/\s+/)[0] ?? "";
}

/** The owner's own name: kept from then on, a Google sign-in no longer replaces it. */
export async function setDisplayName(name: string) {
  await setSetting("profile.name", name.trim());
  await setSetting("profile.nameEdited", true);
}

const CACHE = "engram-profile";
const urls = new Map<string, Promise<string | null>>();

async function loadPhoto(sha: string, mime: string): Promise<string | null> {
  try {
    const cache = typeof caches !== "undefined" ? await caches.open(CACHE) : null;
    const key = `/profile/${sha}`;
    let res = await cache?.match(key);
    if (!res) {
      const net = await apiFetch(`/files/${sha}`);
      if (!net.ok) return null;
      if (cache) {
        // Only the current photo is kept.
        for (const old of await cache.keys()) if (new URL(old.url).pathname !== key) await cache.delete(old);
        await cache.put(key, net.clone());
      }
      res = net;
    }
    // The API serves files as octet-stream; the blob gets the image's own type.
    return URL.createObjectURL(new Blob([await res.arrayBuffer()], { type: mime }));
  } catch {
    return null;
  }
}

export function usePhotoUrl(photo: Profile["photo"]): string | null {
  const [url, setUrl] = useState<string | null>(null);
  const sha = photo?.sha;
  const mime = photo?.mime ?? "image/jpeg";
  useEffect(() => {
    if (!sha) return setUrl(null);
    let alive = true;
    let p = urls.get(sha);
    if (!p) {
      p = loadPhoto(sha, mime);
      urls.set(sha, p);
      void p.then((u) => u == null && urls.delete(sha)); // try again later (offline)
    }
    void booting(p).then((u) => alive && setUrl(u));
    return () => {
      alive = false;
    };
  }, [sha, mime]);
  return url;
}

/** The account's photo, or its initial on the "you" colour when there's no photo (or it hasn't loaded). */
export function Avatar({ profile, size = 30, warn = false }: { profile: Profile; size?: number; warn?: boolean }) {
  const url = usePhotoUrl(profile.photo);
  const style = size === 30 ? undefined : { width: size, height: size, fontSize: Math.round(size * 0.43) };
  return (
    <span className={`avatar${warn ? " warn" : ""}${url ? " photo" : ""}`} style={style} aria-hidden="true">
      {url ? <img src={url} alt="" draggable={false} /> : (profile.name || "E").charAt(0).toUpperCase()}
    </span>
  );
}
